import type { HandleProduct, Piece } from '../doors/drilling';

/**
 * Prices a door order. A port: the live adapter (`doorOrders.ts`) asks the
 * server, which re-prices from the spec and never trusts a figure from the
 * browser. No price is ever worked out here.
 */

/** What the shopper configured; the same shape travels to the basket line's `configuration`. */
export interface DoorOrderSpec {
  rangeId: string;
  /** A catalogue colour id, or `ral` for any colour painted to order. */
  colourId: string;
  /** The colour to paint, as the shopper names it ("RAL 7016 Anthracite grey"); only with `ral`. */
  paintColour?: string;
  /** A handle the shop supplies; `none` drills for the shopper's own handles. */
  handle: HandleProduct;
  softClose: boolean;
  pieces: Piece[];
}

export interface DoorQuoteLine {
  /** Price of one piece, before extras. */
  unit: number;
  total: number;
  /** Set when the piece is costed at a larger standard size (size-banded collections). */
  pricedAs?: { h: number; w: number };
}

export interface DoorQuote {
  currency: string;
  lines: DoorQuoteLine[];
  pieces: number;
  piecesTotal: number;
  extras: {
    handles: number;
    softCloseHinges: number;
    hingeDrilling: number;
    handleDrilling: number;
  };
  extrasTotal: number;
  /** Goods total including VAT. Delivery is priced at checkout, from the address. */
  total: number;
}

export interface DoorPricing {
  quote(spec: DoorOrderSpec): Promise<DoorQuote>;
}

export function formatGbp(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
}
